import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { FlightRecord, UserRole, NewsMessage } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../services/firebase';
import { ref, onValue, remove } from 'firebase/database';
import { TrashIcon, DocumentArrowDownIcon, EnvelopeIcon, PrinterIcon } from '../components/Icons';
import { useFlight } from '../contexts/FlightContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const ReportView = React.memo(forwardRef<HTMLDivElement, { reportData: FlightRecord }>(({ reportData }, ref) => {
    const { user } = useAuth();
    const { flightNo, etd } = reportData.counterDistribution;

    const renderTimestamps = (type: 'crew' | 'pilots') => {
        const crewMovementEntries =
            reportData.crewMovement && typeof reportData.crewMovement === 'object'
            ? Object.entries(reportData.crewMovement)
            : [];
            
        return (
            <div>
                <h4 className="text-xl font-semibold mb-2 capitalize text-blue-300">{type} Movement</h4>
                <ul className="space-y-1 text-sm">
                    {crewMovementEntries.map(([station, data]) => {
                        const timestamps = data?.[type];
                        return (
                            <li key={station} className="border-b border-slate-700 py-1">
                                <strong>{station}:</strong>
                                <div className="pl-4">
                                    {Array.isArray(timestamps) && timestamps.length > 0 ? timestamps.map((ts, i) => (
                                        <div key={i}>
                                            <span>{new Date(ts.time).toLocaleString('en-GB')} by {ts.by}</span>
                                            {ts.modified && <span className="text-amber-400 ml-2">(Modified)</span>}
                                        </div>
                                    )) : 'N/A'}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };
    
    const renderSection = (title: string, data: { [key: string]: string }) => (
        <div className="mt-4">
            <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-2 text-blue-400">{title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="text-sm"><strong>{key}:</strong> {value || 'N/A'}</div>
                ))}
            </div>
        </div>
    );

    const processNewsFeed = (newsFeedObj?: { [id: string]: Omit<NewsMessage, 'id'> }): NewsMessage[] => {
        if (!newsFeedObj) return [];
        return Object.entries(newsFeedObj)
            .map(([id, value]) => ({ id, ...value }))
            .sort((a, b) => a.timestamp - b.timestamp);
    };

    const reportNewsFeed = processNewsFeed(reportData.newsFeed);
    const scannedPassengers = reportData.scannedPassengers || [];

    const bookingSafe = reportData.counterDistribution.booking || { c: '', m: '', inf: '', totalPassengers: '0' };
    const finalFiguresSafe = reportData.counterDistribution.finalFigures || { c: '', m: '', inf: '', totalPassengers: '0', 'Total Bags': '', 'Crew Bags': '', 'Waved Bags': '', 'Wheel Chairs': '', 'Buggy': '', 'Final Fuel': '' };

    const bookingForDisplay = {
        'C': bookingSafe.c,
        'M': bookingSafe.m,
        'Inf': bookingSafe.inf,
        'Total Passengers': bookingSafe.totalPassengers,
    };

    const { c, m, inf, totalPassengers, 'Final Fuel': finalFuel, ...otherFinalFigures } = finalFiguresSafe;
    const finalFiguresForDisplay = {
        'Final Fuel ⛽️': finalFuel,
        'Total Passengers': totalPassengers,
        'C': c,
        'M': m,
        'Inf': inf,
        ...otherFinalFigures
    };
    
    return (
        <div ref={ref} className="p-6 bg-slate-800 text-slate-200 font-sans print-area report-capture">
            <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-white">Flight Operations Report</h1>
                <h2 className="text-2xl text-blue-400">Flight: {flightNo}</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4 text-lg">
                <p><strong>Date:</strong> {new Date(reportData.flightDate).toLocaleDateString('en-GB')}</p>
                <p><strong>ETD:</strong> {etd ? new Date(etd).toLocaleString('en-GB') : 'N/A'}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {renderTimestamps('crew')}
                {renderTimestamps('pilots')}
            </div>
            
            {renderSection('Booking Figures', bookingForDisplay)}
            {renderSection('Counter Distribution', reportData.counterDistribution.counters)}
            {renderSection('Gate Assignments', reportData.counterDistribution.gates)}
            {reportData.atsr && Object.values(reportData.atsr).some(v => v) && renderSection('ATSR', reportData.atsr)}
            {renderSection('Final Figures', finalFiguresForDisplay)}

            {reportNewsFeed.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-3 text-blue-400">Announcements & Comments</h3>
                    <div className="space-y-3 text-sm max-h-60 overflow-y-auto">
                        {reportNewsFeed.map((message) => (
                            <div key={message.id} className="p-2 bg-slate-700/50 rounded-md">
                                <p className="text-slate-100">{message.text}</p>
                                <p className="text-xs text-slate-400 text-right mt-1">
                                    - {message.sender} at {new Date(message.timestamp).toLocaleString('en-GB')}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {scannedPassengers.length > 0 && (
                <div className="mt-8 pt-8 border-t-4 border-slate-700 print:break-before-page">
                    <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-3 text-blue-400">Security Scan Report (ETD Sheet)</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-700 text-sm">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium text-slate-300">Seq.</th>
                                    <th className="px-4 py-2 text-left font-medium text-slate-300">Name</th>
                                    <th className="px-4 py-2 text-left font-medium text-slate-300">Seat</th>
                                    <th className="px-4 py-2 text-left font-medium text-slate-300">Note</th>
                                    <th className="px-4 py-2 text-left font-medium text-slate-300">Scanned At / By</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {scannedPassengers.map(p => (
                                    <tr key={p.sequence}>
                                        <td className="px-4 py-2">{p.sequence}</td>
                                        <td className="px-4 py-2">{p.name}</td>
                                        <td className="px-4 py-2 font-mono">{p.seatNumber}</td>
                                        <td className="px-4 py-2 italic">{p.note || '-'}</td>
                                        <td className="px-4 py-2 text-xs">
                                            {new Date(p.scannedAt).toLocaleString('en-GB')} by {p.scannedBy}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {Array.isArray(reportData.capturedDocs) && reportData.capturedDocs.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-2xl font-bold border-b-2 border-blue-400 pb-1 mb-2 text-blue-400">Captured Documents</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                        {reportData.capturedDocs.map((doc, i) => (
                            <div key={i} className="border border-slate-700 p-2 rounded-lg">
                                <img src={doc.url} alt={`Document ${i+1}`} className="w-full h-auto rounded"/>
                                <p className="text-xs text-center mt-1 text-slate-400">Captured by {doc.by} at {new Date(doc.capturedAt).toLocaleString('en-GB')}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
             <div className="pt-8 mt-8 border-t-2 border-slate-700 text-center text-xs text-slate-500">
                Flight No: {flightNo}, Date: {new Date().toLocaleDateString('en-GB')}, Time: {new Date().toLocaleTimeString('en-GB')} <br/>
                Created By JT-Crew Movement v 7I BY {user?.username || 'System'}
            </div>
        </div>
    );
}));

const generateReportAsText = (reportData: FlightRecord): string => {
    const { flightNo, etd } = reportData.counterDistribution;
    let text = `FLIGHT OPERATIONS REPORT\n`;
    text += `=========================\n\n`;
    text += `Flight: ${flightNo}\n`;
    text += `Date: ${new Date(reportData.flightDate).toLocaleDateString('en-GB')}\n`;
    text += `ETD: ${etd ? new Date(etd).toLocaleString('en-GB') : 'N/A'}\n\n`;

    const renderTimestamps = (type: 'crew' | 'pilots'): string => {
        let tsText = `--- ${type.toUpperCase()} MOVEMENT ---\n`;
        const crewMovementEntries =
            reportData.crewMovement && typeof reportData.crewMovement === 'object'
            ? Object.entries(reportData.crewMovement)
            : [];
        crewMovementEntries.forEach(([station, data]) => {
            const timestamps = data?.[type];
            tsText += `${station}:\n`;
            if (Array.isArray(timestamps) && timestamps.length > 0) {
                timestamps.forEach(ts => {
                    tsText += `  - ${new Date(ts.time).toLocaleString('en-GB')} by ${ts.by}${ts.modified ? ' (Modified)' : ''}\n`;
                });
            } else {
                tsText += `  - N/A\n`;
            }
        });
        return tsText + '\n';
    };

    text += renderTimestamps('crew');
    text += renderTimestamps('pilots');

    const renderSection = (title: string, data: { [key: string]: string | undefined }): string => {
        let sectionText = `--- ${title.toUpperCase()} ---\n`;
        Object.entries(data).forEach(([key, value]) => {
            sectionText += `${key}: ${value || 'N/A'}\n`;
        });
        return sectionText + '\n';
    };

    const bookingSafe = reportData.counterDistribution.booking || {};
    const finalFiguresSafe = reportData.counterDistribution.finalFigures || {};
    
    text += renderSection('Booking Figures', bookingSafe);
    text += renderSection('Counter Distribution', reportData.counterDistribution.counters);
    text += renderSection('Gate Assignments', reportData.counterDistribution.gates);
    if (reportData.atsr && Object.values(reportData.atsr).some(v => v)) {
        text += renderSection('ATSR', reportData.atsr);
    }
    text += renderSection('Final Figures', finalFiguresSafe);

    const scannedPassengers = reportData.scannedPassengers || [];
    if (scannedPassengers.length > 0) {
        text += `--- SECURITY SCAN REPORT (ETD Sheet) ---\n`;
        scannedPassengers.forEach(p => {
            text += `Seq ${p.sequence}: ${p.name}, Seat ${p.seatNumber} (${p.note || 'No note'}). Scanned by ${p.scannedBy} at ${new Date(p.scannedAt).toLocaleString('en-GB')}\n`;
        });
        text += '\n';
    }
    
    text += `\nReport generated by JT-Crew Movement v 7I on ${new Date().toLocaleString('en-GB')}`;
    return text;
};

const formatDateHeader = (dateStr: string): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const reportDate = new Date(dateStr);

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    reportDate.setHours(0, 0, 0, 0);

    let prefix = '';
    if (reportDate.getTime() === today.getTime()) {
        prefix = 'Today - ';
    } else if (reportDate.getTime() === yesterday.getTime()) {
        prefix = 'Yesterday - ';
    }
    
    return prefix + new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
    });
};

const ReportsScreen: React.FC = () => {
    const [groupedFlights, setGroupedFlights] = useState<{ [date: string]: FlightRecord[] }>({});
    const [sortedDates, setSortedDates] = useState<string[]>([]);
    const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
    const { user } = useAuth();
    const { showNotification } = useFlight();
    const [isDeleting, setIsDeleting] = useState(false);
    const reportRef = useRef<HTMLDivElement>(null);

    const [isProcessingPdf, setIsProcessingPdf] = useState(false);

    useEffect(() => {
        const recordsRef = ref(database, 'flightRecords');
        const unsubscribe = onValue(recordsRef, (snapshot) => {
            if (snapshot.exists()) {
                const recordsObj = snapshot.val();
                const flights: FlightRecord[] = Object.values(recordsObj);
                
                const groups: { [date: string]: FlightRecord[] } = {};
                flights.forEach(flight => {
                    const date = flight.flightDate;
                    if (!groups[date]) {
                        groups[date] = [];
                    }
                    groups[date].push(flight);
                });

                for (const date in groups) {
                    groups[date].sort((a, b) => {
                        const timeA = Number(a.id.split('-').pop() || 0);
                        const timeB = Number(b.id.split('-').pop() || 0);
                        return timeB - timeA;
                    });
                }

                setGroupedFlights(groups);
                setSortedDates(Object.keys(groups).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()));

            } else {
                setGroupedFlights({});
                setSortedDates([]);
            }
        });

        return () => unsubscribe();
    }, []);
    
    const allFlights = sortedDates.flatMap(date => groupedFlights[date]);
    const selectedFlight = allFlights.find(f => f.id === selectedFlightId);

    const handleDeleteReport = useCallback(async (flightId: string) => {
        if (window.confirm('Are you sure you want to permanently delete this report? This action cannot be undone.')) {
            setIsDeleting(true);
            try {
                const recordRef = ref(database, `flightRecords/${flightId}`);
                await remove(recordRef);
                setSelectedFlightId(null);
                showNotification('Report deleted successfully.');
            } catch (error) {
                console.error("Error deleting report:", error);
                showNotification('Failed to delete report.', 'error');
            } finally {
                setIsDeleting(false);
            }
        }
    }, [showNotification]);

    const handleExportPdf = useCallback(async () => {
        if (!selectedFlight || isProcessingPdf) return;
        setIsProcessingPdf(true);

        const reportElement = reportRef.current;
        if (!reportElement) {
            showNotification('Report element not found.', 'error');
            setIsProcessingPdf(false);
            return;
        }

        reportElement.classList.add('report-capture');
        document.body.classList.add('report-capture');

        try {
            const canvas = await html2canvas(reportElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                windowHeight: reportElement.scrollHeight,
                windowWidth: reportElement.scrollWidth,
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'p',
                unit: 'px',
                format: [canvas.width, canvas.height]
            });
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            const flightNo = selectedFlight.counterDistribution.flightNo;
            pdf.save(`Report-${flightNo}-${new Date().toISOString().split('T')[0]}.pdf`);
            showNotification('PDF exported successfully.', 'success');
        } catch (error) {
            console.error("Error generating PDF:", error);
            showNotification('Failed to generate PDF.', 'error');
        } finally {
            reportElement.classList.remove('report-capture');
            document.body.classList.remove('report-capture');
            setIsProcessingPdf(false);
        }
    }, [selectedFlight, isProcessingPdf, showNotification]);

    const handleEmail = useCallback(() => {
        if (!selectedFlight) return;

        const reportText = generateReportAsText(selectedFlight);
        const subject = `Flight Report: ${selectedFlight.counterDistribution.flightNo}`;
        
        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(reportText)}`;

        try {
            window.open(mailtoLink, '_self');
        } catch(e) {
            console.error("Failed to open mail client", e);
            showNotification("Could not open email client. The report might be too long.", 'error');
        }
    }, [selectedFlight, showNotification]);

    return (
        <div className="flex flex-col md:flex-row gap-6 h-full">
            {/* Sidebar for report selection */}
            <div className="w-full md:w-80 lg:w-96 flex-shrink-0 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-3 no-print space-y-4 flex flex-col">
                <h2 className="text-xl font-bold text-blue-400 border-b border-slate-700 pb-2">Archived Reports</h2>
                <div className="flex-grow overflow-y-auto pr-2 space-y-4">
                    {sortedDates.length > 0 ? sortedDates.map(date => (
                        <div key={date}>
                            <h3 className={`font-semibold sticky top-0 bg-slate-900/80 backdrop-blur-sm py-1 px-2 rounded ${new Date(date).toDateString() === new Date().toDateString() ? 'text-amber-300' : 'text-slate-300'}`}>
                                {formatDateHeader(date)}
                            </h3>
                            <div className="mt-1 space-y-1">
                                {groupedFlights[date].map(flight => {
                                    const timestamp = Number(flight.id.split('-').pop() || 0);
                                    const creationTime = new Date(timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

                                    return (
                                        <button
                                            key={flight.id}
                                            onClick={() => setSelectedFlightId(flight.id)}
                                            className={`w-full text-left p-2 rounded-md transition-colors duration-150 ${selectedFlightId === flight.id ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold' : 'bg-slate-700/50 hover:bg-slate-700 text-slate-200'}`}
                                        >
                                            <p className="font-bold">{flight.counterDistribution.flightNo}</p>
                                            <p className="text-xs text-slate-400">{creationTime}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )) : (
                        <p className="text-slate-400 text-center pt-10">No reports found.</p>
                    )}
                </div>
            </div>

            {/* Main content area */}
            <div className="flex-grow flex flex-col">
                {selectedFlight ? (
                    <>
                        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-3 flex flex-wrap items-center justify-between gap-4 mb-4 no-print">
                           <span className="font-bold text-white">Selected: {selectedFlight.counterDistribution.flightNo}</span>
                           <div className="flex items-center gap-4 sm:gap-6">
                                {user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && (
                                    <button onClick={() => handleDeleteReport(selectedFlight.id)} disabled={isDeleting} className="flex flex-col items-center text-red-400 hover:text-red-300 transition-colors disabled:text-slate-500 disabled:cursor-wait">
                                        <TrashIcon className="w-6 h-6" />
                                        <span className="text-xs mt-1">Delete</span>
                                    </button>
                                )}
                                <button onClick={handleExportPdf} disabled={isProcessingPdf} className="flex flex-col items-center text-green-400 hover:text-green-300 transition-colors disabled:text-slate-500 disabled:cursor-wait">
                                    <DocumentArrowDownIcon className="w-6 h-6"/>
                                    <span className="text-xs mt-1">PDF</span>
                                </button>
                                <button onClick={handleEmail} className="flex flex-col items-center text-sky-400 hover:text-sky-300 transition-colors">
                                    <EnvelopeIcon className="w-6 h-6"/>
                                    <span className="text-xs mt-1">Email</span>
                                </button>
                                <button onClick={() => window.print()} className="flex flex-col items-center text-blue-400 hover:text-blue-300 transition-colors">
                                    <PrinterIcon className="w-6 h-6"/>
                                    <span className="text-xs mt-1">Print</span>
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow overflow-y-auto bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
                            <ReportView ref={reportRef} reportData={selectedFlight} />
                        </div>
                    </>
                ) : (
                    <div className="flex-grow flex items-center justify-center text-center p-10 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
                        <p className="text-slate-400 text-lg">Please select a flight from the archive to view the report.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsScreen;